import api from '@/shared/utils/apiConfig';

export type ContactLogSource = 'manual' | 'omicall';
export type ContactLogChannel =
  | 'phone'
  | 'zalo'
  | 'sms'
  | 'email'
  | 'in_person'
  | 'other';
export type ContactLogOutcome =
  | 'reached'
  | 'no_answer'
  | 'callback'
  | 'wrong_number'
  | 'interested'
  | 'not_interested'
  | 'other';

export interface ContactCall {
  callUuid: string;
  direction?: 'inbound' | 'outbound' | 'local';
  status: 'ringing' | 'answered' | 'hangup' | 'missed';
  sipUser?: string;
  hotline?: string;
  phoneNumber?: string;
  ringingAt?: string;
  answeredAt?: string;
  endedAt?: string;
  durationSec?: number;
  recordingUrl?: string;
}

export interface ContactStaffSummary {
  id: string;
  firstName: string;
  lastName: string;
  staffCode?: string;
}

export interface ContactCandidate {
  id: string;
  name: string;
  kind: 'lead' | 'patient';
}

export interface ContactLog {
  id: string;
  source: ContactLogSource;
  channel: ContactLogChannel;
  outcome: ContactLogOutcome;
  text?: string;
  contactStaffId?: string;
  contactStaff?: ContactStaffSummary;
  contactedAt: string;
  callbackDueAt?: string;
  createdBy?: { id: string; name: string };
  createdAt: string;
  leadId?: string;
  patientId?: string;
  subject?: ContactCandidate;
  call?: ContactCall;
  candidates?: ContactCandidate[];
}

export interface MySipCredentials {
  sipRealm: string;
  sipUser: string;
  sipPassword: string;
  hotline?: string;
}

export interface RemoteExtension {
  sipUser: string;
  sipRealm?: string;
  fullName?: string;
  email?: string;
  assignedStaffId?: string;
}

export interface AssignedExtension {
  id: string;
  staffId: string;
  staffName: string;
  sipUser: string;
  sipRealm?: string;
  enabled: boolean;
}

export interface RemoteContactHit {
  name: string;
  kind: 'patient' | 'lead';
  id: string;
}

interface ApiEnvelope<T> {
  success: boolean;
  data?: T;
  message?: string;
}

export const callsAPI = {
  getMySip: async (): Promise<ApiEnvelope<MySipCredentials | null>> => {
    const response = await api.get('/calls/me/sip');
    return response.data;
  },

  lookup: async (phone: string): Promise<ApiEnvelope<{ name: string; matches: RemoteContactHit[] }>> => {
    const response = await api.get('/calls/lookup', { params: { phone } });
    return response.data;
  },

  listExtensions: async (): Promise<
    ApiEnvelope<{ assigned: AssignedExtension[]; remote: RemoteExtension[] }>
  > => {
    const response = await api.get('/calls/extensions');
    return response.data;
  },

  assignExtension: async (payload: {
    staffId: string;
    sipUser: string;
  }): Promise<ApiEnvelope<AssignedExtension>> => {
    const response = await api.post('/calls/extensions', payload);
    return response.data;
  },

  unassignExtension: async (staffId: string): Promise<ApiEnvelope<void>> => {
    const response = await api.delete(`/calls/extensions/${staffId}`);
    return response.data;
  },

  listContacts: async (params: {
    leadId?: string;
    patientId?: string;
  }): Promise<ApiEnvelope<ContactLog[]>> => {
    const response = await api.get('/calls/contacts', { params });
    return response.data;
  },

  getContact: async (id: string): Promise<ApiEnvelope<ContactLog>> => {
    const response = await api.get(`/calls/contacts/${id}`);
    return response.data;
  },

  createContact: async (payload: {
    leadId?: string;
    patientId?: string;
    channel: ContactLogChannel;
    outcome: ContactLogOutcome;
    text?: string;
    contactStaffId: string;
    contactedAt: string;
    callbackDueAt?: string;
  }): Promise<ApiEnvelope<ContactLog>> => {
    const response = await api.post('/calls/contacts', payload);
    return response.data;
  },

  linkContact: async (
    id: string,
    payload: { leadId?: string; patientId?: string }
  ): Promise<ApiEnvelope<ContactLog>> => {
    const response = await api.post(`/calls/contacts/${id}/link`, payload);
    return response.data;
  },

  recordingUrl: (id: string): string => `/api/calls/contacts/${id}/recording`,

  getRecordingUrl: async (id: string): Promise<string> => {
    const response = await api.get(`/calls/contacts/${id}/recording`);
    const url = (response.data as ApiEnvelope<{ url?: string }>)?.data?.url;
    if (!url) throw new Error('Recording not available');
    return url;
  },

  syncHistory: async (payload: {
    from: number;
    to: number;
  }): Promise<ApiEnvelope<{ upserted: number; scanned: number }>> => {
    const response = await api.post('/calls/history/sync', payload);
    return response.data;
  },
};
